import {
  cloneElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import { cx } from './classNames';
import { Portal } from './Portal';
import { useOutsideDismiss, usePopupPosition } from './usePopupPosition';

export type TooltipPosition = 'BottomCenter' | 'BottomLeft' | 'BottomRight' | 'TopCenter';

/** Pendant zu `SfTooltip`. */
export interface TooltipProps {
  content: ReactNode;
  /** Ankerelement — bekommt Ref und Ereignisse angeheftet. */
  children: ReactElement;
  /** Standard `Hover`. */
  opensOn?: 'Hover' | 'Click';
  /** Verzögerung vor dem Schließen in Millisekunden. */
  closeDelay?: number;
  cssClass?: string;
  width?: string;
  /** Standard `TopCenter` — wie der Syncfusion-Standard. */
  position?: TooltipPosition;
  /**
   * `true` überlässt die Lage den CSS-Regeln aus REQ-05 (Profil- und
   * Navbar-Tooltip) statt `left`/`top` zu setzen.
   */
  useCssPosition?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
}

interface AnchorProps {
  ref?: Ref<HTMLElement>;
  onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

export function Tooltip(props: TooltipProps) {
  const opensOn = props.opensOn ?? 'Hover';
  const position = props.position ?? 'TopCenter';
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);

  const clearTimer = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const close = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimer();
    const delay = props.closeDelay ?? 0;
    if (delay === 0) {
      setOpen(false);
      return;
    }
    timer.current = window.setTimeout(() => {
      setOpen(false);
    }, delay);
  }, [props.closeDelay]);

  useEffect(() => clearTimer, []);
  useOutsideDismiss(open && opensOn === 'Click', close, [anchorRef, popupRef]);

  const popupStyle = usePopupPosition({
    open,
    anchorRef,
    popupRef,
    align: position,
    enabled: props.useCssPosition !== true,
  });

  const child = props.children as ReactElement<AnchorProps>;
  const anchor = cloneElement(child, {
    ref: anchorRef,
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      child.props.onMouseEnter?.(event);
      if (opensOn === 'Hover') {
        clearTimer();
        setOpen(true);
      }
    },
    onMouseLeave: (event: MouseEvent<HTMLElement>) => {
      child.props.onMouseLeave?.(event);
      if (opensOn === 'Hover') scheduleClose();
    },
    onClick: (event: MouseEvent<HTMLElement>) => {
      child.props.onClick?.(event);
      if (opensOn === 'Click') setOpen((value) => !value);
    },
  });

  const arrowSide = position === 'TopCenter' ? 'e-tip-bottom' : 'e-tip-top';

  return (
    <>
      {anchor}
      {open ? (
        <Portal>
          <div
            id={props.id}
            ref={popupRef}
            role="tooltip"
            className={cx('e-tooltip-wrap', 'e-popup', props.cssClass, props.className)}
            style={{ width: props.width, ...popupStyle, ...props.style }}
            onMouseEnter={clearTimer}
            onMouseLeave={() => {
              if (opensOn === 'Hover') scheduleClose();
            }}
          >
            <div className="e-tip-content">{props.content}</div>
            <div className={cx('e-arrow-tip', arrowSide)}>
              <div className={cx('e-arrow-tip-outer', arrowSide)} />
              <div className={cx('e-arrow-tip-inner', arrowSide)} />
            </div>
          </div>
        </Portal>
      ) : null}
    </>
  );
}

export default Tooltip;
